import { ref } from 'vue'

let idCounter = 0

/**
 * Generates unique IDs for accessibility and component association
 *
 * Creates globally unique identifiers for associating form labels with inputs,
 * connecting ARIA descriptions, and other accessibility requirements.
 *
 * @example Basic usage
 * ```typescript
 * const inputId = useId('email')
 * // Returns: ref('email-1')
 * ```
 *
 * @example In a form component
 * ```vue
 * <script setup>
 * const inputId = useId('password')
 * </script>
 *
 * <template>
 *   <label :for="inputId">Password</label>
 *   <input :id="inputId" type="password" />
 * </template>
 * ```
 *
 * @param prefix - Optional prefix for the generated ID (default: 'ui')
 * @returns A ref containing a unique ID string
 * @see {@link packages/ui-vue/src/composables/useId.ts:1}
 */
export function useId(prefix = 'ui') {
  const id = ref(`${prefix}-${++idCounter}`)
  return id
}

/**
 * Creates multiple related IDs with a shared prefix
 *
 * Useful for form fields that need multiple associated elements
 * (input, label, error, description).
 *
 * @example Basic usage
 * ```typescript
 * const ids = useIds('field', ['input', 'label', 'error'])
 * // Returns: { input: 'field-1-input', label: 'field-1-label', error: 'field-1-error' }
 * ```
 *
 * @example In a custom form component
 * ```vue
 * <script setup>
 * const ids = useIds('custom-select', ['trigger', 'menu', 'description'])
 * </script>
 *
 * <template>
 *   <button :id="ids.trigger" :aria-describedby="ids.description">Select</button>
 *   <ul :id="ids.menu" role="listbox">...</ul>
 *   <span :id="ids.description">Choose an option</span>
 * </template>
 * ```
 *
 * @param basePrefix - Base prefix for all IDs
 * @param suffixes - Array of suffix strings for each ID
 * @returns Object with keys matching suffixes, values are ID strings
 * @see {@link packages/ui-vue/src/composables/useId.ts:1}
 */
export function useIds<T extends readonly string[]>(basePrefix: string, suffixes: T): Record<T[number], string> {
  const baseId = ++idCounter
  const ids: Record<string, string> = {}

  for (const suffix of suffixes) {
    ids[suffix] = `${basePrefix}-${baseId}-${suffix}`
  }

  return ids as Record<T[number], string>
}

/**
 * Convenience composable for form field IDs
 *
 * Creates a set of commonly needed form field identifiers for proper
 * accessibility associations.
 *
 * @example Basic usage
 * ```typescript
 * const fieldIds = useFormFieldIds('email')
 * // Returns: {
 * //   input: 'email-1-input',
 * //   label: 'email-1-label',
 * //   error: 'email-1-error',
 * //   description: 'email-1-description'
 * // }
 * ```
 *
 * @example Complete accessible form field
 * ```vue
 * <script setup>
 * const fieldIds = useFormFieldIds('username')
 * const error = ref('')
 * </script>
 *
 * <template>
 *   <label :for="fieldIds.input" :id="fieldIds.label">
 *     Username
 *   </label>
 *   <input
 *     :id="fieldIds.input"
 *     :aria-labelledby="fieldIds.label"
 *     :aria-describedby="error ? fieldIds.error : fieldIds.description"
 *     :aria-invalid="!!error"
 *   />
 *   <span :id="fieldIds.description">Enter your username</span>
 *   <span v-if="error" :id="fieldIds.error" role="alert">{{ error }}</span>
 * </template>
 * ```
 *
 * @param prefix - Prefix for the field (e.g., 'email', 'password')
 * @returns Object with input, label, error, and description IDs
 * @see {@link packages/ui-vue/src/composables/useId.ts:1}
 */
export function useFormFieldIds(prefix: string) {
  return useIds(prefix, ['input', 'label', 'error', 'description'] as const)
}
